import React, { useState, useEffect, useCallback } from 'react';
import classnames from 'classnames';
import { Formik } from 'formik';

import { hasRoleSalesRep, hasRoleSalesRepManager } from 'common/utility/userRoles';

import { actionDataTableReset } from 'client/helpers/dataTables';

import {
  UTILITY_ACTIVATION_ITEM_ORDER_TYPE_NON_DELAYED_ORDERS,
  UTILITY_ACTIVATION_ITEM_ORDER_TYPE_DELAYED_ORDERS,
  UTILITY_ACTIVATION_ITEM_ORDER_TYPE_FAR_OUT_MOVE_INS,
  UTILITY_MAX_ORDERS_PER_SALES_REP,
} from 'common/utility/constants';

import { SR_API_ACTION_UTILITY_ACTIVATION_START_ORDER } from 'api/utilityActionTypes';

import { DATA_TABLE_SOURCE_UTILITY_ORDERS_HITLIST } from 'common/dataTableConstants';

import useBranch from 'client/hooks/useBranch';
import useApiState from 'client/hooks/useApiState';
import requestUtilityApi from 'client/actions/requestUtilityApi';
import UtilityActivationActions from 'client/actions/UtilityActivation';

import SrFormikField, { SR_FIELD_THEME_ADOBE } from 'client/components/SrFormikField';
import SrText from 'client/components/SrText';
import SrLink from 'client/components/SrLink/SrLink';
import SrErrorMessage from 'client/components/SrErrorMessage/SrErrorMessage';
import SrDataTableConnected from 'client/components/SrDataTableConnected';
import UPOrdersHitlistTableRow from 'client/components/UPOrdersHitlistTable/UPOrdersHitlistTableRow';

import './UPOrdersHitlistTable.less';

const FILTER_MODE_TO_TEXT = {
  [UTILITY_ACTIVATION_ITEM_ORDER_TYPE_NON_DELAYED_ORDERS]: 'Non-Delayed Orders',
  [UTILITY_ACTIVATION_ITEM_ORDER_TYPE_DELAYED_ORDERS]: 'Delayed Orders',
  [UTILITY_ACTIVATION_ITEM_ORDER_TYPE_FAR_OUT_MOVE_INS]: 'Far Out Move-Ins',
};

const SORT_MODE_LEASE_START_DATE = 'leaseStartDate';
const SORT_MODE_ACCOUNT_REQUESTED_DATE = 'accountRequestedDate';

const SORT_MODE_TO_TEXT = {
  [SORT_MODE_LEASE_START_DATE]: 'Move-In Date',
  [SORT_MODE_ACCOUNT_REQUESTED_DATE]: 'Requested Date',
};

function getDataTableSettingsFromProps({
  searchTerm,
  orderType,
  sortMode,
  utilitySalesRepId,
  currentUtilitySalesRepId,
}) {
  const sort =
    sortMode === SORT_MODE_ACCOUNT_REQUESTED_DATE
      ? [
          {
            accountRequestedAt: 'asc',
          },
        ]
      : [
          {
            salesRepPriority: 'desc',
          },
          {
            partnerDoorsUnderManagement: 'desc',
          },
          {
            leaseStartDate: 'asc',
          },
        ];

  const dataTableSettings = {
    dataSource: DATA_TABLE_SOURCE_UTILITY_ORDERS_HITLIST,
    rootViewId: DATA_TABLE_SOURCE_UTILITY_ORDERS_HITLIST,
    initialViewSettings: {
      sort: sort,
      customParams: {
        itemOrders: {
          orderType,
          currentUtilitySalesRepId: currentUtilitySalesRepId,
        },
      },
      filter: {
        orderType,
        utilitySalesRepId,
      },
      fuzzy: !searchTerm
        ? null
        : {
            queryString: searchTerm,
            columns: ['address.display', 'user.firstName', 'user.lastName', 'user.phone'],
          },
    },
    loadAllData: true,
    itemsPropName: 'utilityActivations',
    resetOnUnmount: true,
  };

  if (searchTerm) {
    delete dataTableSettings.initialViewSettings.customParams;
    delete dataTableSettings.initialViewSettings.filter;
  }

  return dataTableSettings;
}

const UPOrdersHitlistTable = ({
  dispatch,
  currentUser,
  utilitySalesReps,
  utilityDealers,
  utilityDelayedReasons,
  orderType,
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [utilitySalesRepId, setUtilitySalesRepId] = useState(undefined);
  const [sortMode, setSortMode] = useState(SORT_MODE_LEASE_START_DATE);
  const isSalesRep = hasRoleSalesRep(currentUser.roles);
  const isSaleRepManager = hasRoleSalesRepManager(currentUser.roles);
  const { ordersBySalesRep } = useBranch({
    ordersBySalesRep: ['utilityActivations', 'countBySalesRep', currentUser.id],
  });
  const startOrderApiState = useApiState(SR_API_ACTION_UTILITY_ACTIVATION_START_ORDER);
  const salesRepNonDelayedOrders =
    ordersBySalesRep?.[UTILITY_ACTIVATION_ITEM_ORDER_TYPE_NON_DELAYED_ORDERS] || [];
  const salesRepDelayedOrders =
    ordersBySalesRep?.[UTILITY_ACTIVATION_ITEM_ORDER_TYPE_DELAYED_ORDERS] || [];
  const salesRepOrdersCount = salesRepNonDelayedOrders.length + salesRepDelayedOrders.length;
  const currentUtilitySalesRepId = isSalesRep ? currentUser.id : 0;

  const onVisibilityChange = useCallback(() => {
    dispatch(UtilityActivationActions.getOrderHitlistCountBySalesRep, {
      utilitySalesRepId: currentUser.id,
    });
  }, [dispatch, currentUser.id]);

  useEffect(() => {
    global.document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      global.document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [onVisibilityChange]);

  return (
    <SrDataTableConnected
      {...getDataTableSettingsFromProps({
        searchTerm,
        orderType,
        sortMode,
        utilitySalesRepId,
        currentUtilitySalesRepId,
      })}
    >
      {({
        utilityActivations,
        dataTableApiState,
        reloadItems,
        viewSettings,
        onInfiniteScrollWrapperRef,
      }) => {
        const dataTableOrderType = viewSettings?.customParams?.itemOrders?.orderType;

        const onReloadItem = ({ utilityActivationId, orderType }) => {
          return Promise.all([
            reloadItems({
              count: 1,
              filter: {
                id: utilityActivationId,
              },
              customParams: {
                itemOrders: {
                  orderType: orderType || dataTableOrderType,
                  currentUtilitySalesRepId,
                },
              },
            }),
            dispatch(UtilityActivationActions.getOrderHitlistCountBySalesRep, {
              utilitySalesRepId: currentUser.id,
            }),
          ]);
        };

        const onRemoveItem = (utilityActivation) => {
          return Promise.all([
            dispatch(({ state }) => {
              const dataSource = DATA_TABLE_SOURCE_UTILITY_ORDERS_HITLIST;
              const statePath = ['dataTables', dataSource, 'items'];
              state.set(
                statePath,
                state.get(statePath).filter((item) => item.id !== utilityActivation.id),
              );
            }),
            dispatch(UtilityActivationActions.getOrderHitlistCountBySalesRep, {
              utilitySalesRepId: currentUser.id,
            }),
          ]);
        };

        const onResetTable = () => {
          dispatch(actionDataTableReset, {
            dataSource: DATA_TABLE_SOURCE_UTILITY_ORDERS_HITLIST,
          });
        };

        const onChangeUtilitySalesRep = ({
          utilityActivation,
          utilityActivationItem,
          utilitySalesRepId,
        }) => {
          return dispatch(requestUtilityApi, {
            action: SR_API_ACTION_UTILITY_ACTIVATION_START_ORDER,
            payload: {
              utilityActivationId: utilityActivation.id,
              utilityActivationItemId: utilityActivationItem.id,
              utilitySalesRepId: utilitySalesRepId,
            },
            onError: (error) => {
              global.alert(error.message);
            },
          }).then(() => {
            return onReloadItem({
              utilityActivationId: utilityActivation.id,
            });
          });
        };

        return (
          <div>
            {salesRepOrdersCount >= UTILITY_MAX_ORDERS_PER_SALES_REP && (
              <SrErrorMessage>
                You already have {UTILITY_MAX_ORDERS_PER_SALES_REP} orders assigned to you.
                Please finish those first before getting a new one.
              </SrErrorMessage>
            )}
            <Formik>
              {(formikBag) => {
                return (
                  <div styleName="headerBox">
                    <div styleName="headerFilters">
                      <div>
                        {Object.keys(FILTER_MODE_TO_TEXT).map((key) => (
                          <SrLink
                            styleName={classnames('sortModeLink', {
                              active: dataTableOrderType === key,
                            })}
                            onClick={() => {
                              setSearchTerm('');
                              formikBag.setFieldValue('searchTerm', '');
                            }}
                            key={key}
                            to={`/utility-profit/admin/dashboard/order-hitlist/${key}`}
                          >
                            {FILTER_MODE_TO_TEXT[key]}
                          </SrLink>
                        ))}
                      </div>
                      <div>
                        <div styleName="filters">
                          <SrFormikField
                            formField={{
                              id: 'salesRepId',
                              type: 'select',
                              properties: {
                                placeholder: 'Sales Rep',
                                noMargin: true,
                                enableResetOption: true,
                                choices: utilitySalesReps.map((utilitySalesRep) => ({
                                  value: utilitySalesRep.id,
                                  label: `${utilitySalesRep.firstName} ${utilitySalesRep.lastName}`,
                                })),
                                onChange: (event) => {
                                  setUtilitySalesRepId(
                                    event.target.value
                                      ? parseInt(event.target.value, 10)
                                      : undefined,
                                  );
                                },
                              },
                            }}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                          <SrFormikField
                            formField={{
                              id: 'searchTerm',
                              type: 'short_text',
                              properties: {
                                placeholder: 'Search Address or Tenant (Name or Phone)',
                                noMargin: true,
                                onChange: (event) => {
                                  setSearchTerm((event.target?.value || '').trim());
                                },
                                style: {
                                  width: '315px',
                                },
                              },
                            }}
                            formikBag={formikBag}
                            theme={SR_FIELD_THEME_ADOBE}
                          />
                        </div>
                      </div>
                    </div>
                    {UTILITY_ACTIVATION_ITEM_ORDER_TYPE_NON_DELAYED_ORDERS ===
                      dataTableOrderType && (
                      <div styleName="headerSorts">
                        <div>
                          <SrText font="greycliff" size="18" weight="500">
                            Sort:
                          </SrText>
                          {Object.keys(SORT_MODE_TO_TEXT).map((sortModeValue) => {
                            const isActive = sortMode === sortModeValue;
                            return (
                              <SrText
                                key={sortModeValue}
                                onClick={
                                  isActive
                                    ? null
                                    : () => {
                                        setSortMode(sortModeValue);
                                      }
                                }
                                weight={isActive ? '800' : '500'}
                                decoration={isActive ? 'underline' : null}
                                size="18"
                                font="greycliff"
                                cursor="pointer"
                                paddingLeft="5px"
                              >
                                {SORT_MODE_TO_TEXT[sortModeValue]}
                              </SrText>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                );
              }}
            </Formik>
            <div ref={onInfiniteScrollWrapperRef}>
              {utilityActivations.map((utilityActivation) => (
                <UPOrdersHitlistTableRow
                  key={utilityActivation.id}
                  dispatch={dispatch}
                  utilityActivation={utilityActivation}
                  utilityDealers={utilityDealers}
                  utilityDelayedReasons={utilityDelayedReasons}
                  currentUser={currentUser}
                  isSalesRep={isSalesRep}
                  isSaleRepManager={isSaleRepManager}
                  onReloadItem={onReloadItem}
                  onRemoveItem={onRemoveItem}
                  onResetTable={onResetTable}
                  onChangeUtilitySalesRep={onChangeUtilitySalesRep}
                  orderType={dataTableOrderType}
                  isSortModeAccountRequestedDate={
                    sortMode === SORT_MODE_ACCOUNT_REQUESTED_DATE &&
                    dataTableOrderType === UTILITY_ACTIVATION_ITEM_ORDER_TYPE_NON_DELAYED_ORDERS
                  }
                  salesRepOrdersCount={salesRepOrdersCount}
                  salesRepNonDelayedOrders={salesRepNonDelayedOrders}
                  startOrderApiState={startOrderApiState}
                />
              ))}
              {dataTableApiState.inProgress && <div className="loading-ellipses clearfix" />}
              {!dataTableApiState.inProgress && utilityActivations.length === 0 && (
                <div styleName="noResults">No results found</div>
              )}
            </div>
          </div>
        );
      }}
    </SrDataTableConnected>
  );
};

UPOrdersHitlistTable.displayName = 'UPOrdersHitlistTable';

export default UPOrdersHitlistTable;
